import type Cell from "@/models/cell";
import CellRepository from "@/repositories/cellRepository";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useAuthStore } from "./auth";
import { useGardenStore } from "./garden";

export const useCellsStore = defineStore("cells", () => {
  const cells = ref<Cell[][]>([]);
  const gardenId = ref<number | null>(null);
  const isLoaded = ref(false);
  const isLoading = ref(false);

  const areCellsLoaded = computed(() => isLoaded.value);
  const arableCellCount = computed(() =>
    cells.value.reduce((sum, row) => sum + row.filter((c) => c.isOptimizable).length, 0)
  );

  // Loads the active garden's cells. No-op if cells are already cached for that
  // garden. Pass `{ force: true }` to refetch (e.g. after save / optimization).
  async function loadCells(payload?: { force?: boolean }) {
    const garden = useGardenStore().garden;
    if (!garden || !useAuthStore().isAuthenticated) return;
    if (isLoading.value) return;
    if (!payload?.force && isLoaded.value && gardenId.value === garden.id) return;

    isLoading.value = true;
    try {
      cells.value = await CellRepository.list(garden.id);
      gardenId.value = garden.id;
      isLoaded.value = true;
    } finally {
      isLoading.value = false;
    }
  }

  function resetCells() {
    cells.value = [];
    gardenId.value = null;
    isLoaded.value = false;
    isLoading.value = false;
  }

  return { cells, areCellsLoaded, arableCellCount, loadCells, resetCells };
});
